import React, { useEffect, useRef, useState, useCallback } from "react";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { API } from "api/axios.config";

const textField = "matterRef";
const keyField = "matterId";
const pageSize = 30;
const searchDebounceMs = 300;

const emptyItem = { [textField]: "loading ..." };
const loadingData = Array.from({ length: pageSize }, () => ({ ...emptyItem }));

const pageItemCount = (total, skip) => Math.min(pageSize, Math.max(0, total - skip));

const pickMatterFromEvent = (event) => {
    if (event == null) return null;
    if (Object.prototype.hasOwnProperty.call(event, "value")) return event.value;
    if (event.target?.value !== undefined) return event.target.value;
    return null;
};

const MattersCombobox = ({
    value,
    onChangematter,
    controlled = false,
    openMattersOnly = false,
    deferChangeUntilClose = false,
    ...props
}) => {
    const dataCaching = useRef([]);
    const pendingRequest = useRef(undefined);
    const requestStarted = useRef(false);
    const skipRef = useRef(0);
    const totalRef = useRef(0);
    const filterDebounce = useRef();
    const comboBoxRef = useRef(null);
    const pendingMatterRef = useRef(null);
    const lastFilterRef = useRef("");

    const [data, setData] = useState([]);
    const [internalValue, setInternalValue] = useState(null);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [popupOpen, setPopupOpen] = useState(false);
    const [tempMatter, setTempMatter] = useState(null);

    const resetCache = useCallback(() => {
        dataCaching.current.length = 0;
    }, []);

    const resetForSearch = useCallback(() => {
        resetCache();
        totalRef.current = 0;
        skipRef.current = 0;
        setSkip(0);
        setData(loadingData);
        setTotal((t) => (t > 0 ? t : pageSize));
    }, [resetCache]);

    const getCachedData = useCallback((skip) => {
        const count = pageItemCount(totalRef.current, skip);
        return Array.from({ length: count }, (_, i) => {
            const cached = dataCaching.current[skip + i];
            return cached?.matterId ? cached : { ...emptyItem };
        });
    }, []);

    const shouldRequestData = useCallback((skip) => {
        const count = pageItemCount(totalRef.current, skip);
        for (let i = 0; i < count; i++) {
            if (!dataCaching.current[skip + i]?.matterId) return true;
        }
        return false;
    }, []);

    const requestData = useCallback(
        async (skip = 0, search = "") => {
            if (requestStarted.current) {
                clearTimeout(pendingRequest.current);
                return new Promise((resolve) => {
                    pendingRequest.current = setTimeout(
                        () => requestData(skip, search).then(resolve),
                        50,
                    );
                });
            }

            requestStarted.current = true;
            if (skip === 0) {
                setData(loadingData);
                setTotal((t) => (t > 0 ? t : pageSize));
                skipRef.current = 0;
                setSkip(0);
            }
            setLoading(true);

            try {
                const query = new URLSearchParams({
                    ...(search && { search }),
                    page: Math.floor(skip / pageSize) + 1,
                    limit: pageSize,
                    active: true,
                    ...(openMattersOnly && { Closed: false }),
                }).toString();

                const { data: response } = await API.get(`/matters/search?${query}`);
                const items = response.data.map((item) => ({
                    [keyField]: item.matterId,
                    [textField]: item.matterRef,
                    ...item,
                }));

                items.forEach((item, i) => {
                    dataCaching.current[i + skip] = item;
                });

                const resolvedTotal = response.total ?? response.data.length;
                totalRef.current = resolvedTotal;
                setTotal(resolvedTotal);

                if (skip === skipRef.current) {
                    setData(items);
                }
            } catch (error) {
                console.error("Failed to fetch matters:", error);
                totalRef.current = 0;
                setData([]);
                setTotal(0);
            } finally {
                requestStarted.current = false;
                setLoading(false);
            }
        },
        [openMattersOnly],
    );

    const itemRender = (li, itemProps) => {
        const { dataItem } = itemProps;
        const content = (
            <div className="d-flex justify-content-center align-items-center flex-column w-100">
                <div
                    className="d-flex flex-column justify-content-center ms-2 w-100 my-2 ps-2"
                    style={{ backgroundColor: "#F8F8F8", borderRadius: "5px", padding: "6px" }}>
                    <div className="d-flex">
                        <span className="fw-semibold fs-sm">{dataItem.matterRef}</span>
                        <span className="ms-2 fw-semibold fs-sm text-decoration-underline">
                            {dataItem.clientName}
                        </span>
                    </div>
                    <span className="fs-sm">{dataItem.details}</span>
                </div>
                <hr className="ms-1 m-0" style={{ width: "95%", border: "0.5px solid #E1E1E1" }} />
            </div>
        );
        return React.cloneElement(li, li.props, content);
    };

    useEffect(() => {
        if (!props.disabled) requestData(0, "");
        return () => {
            clearTimeout(filterDebounce.current);
            resetCache();
        };
    }, [requestData, props.disabled, resetCache]);

    useEffect(() => {
        if (props.reset) {
            clearTimeout(filterDebounce.current);
            setInternalValue(null);
            lastFilterRef.current = "";
            setFilter("");
            resetForSearch();
            requestData(0, "");
        }
    }, [props.reset, resetForSearch, requestData]);

    useEffect(() => {
        setInternalValue(value ?? null);
    }, [value]);

    useEffect(() => {
        if (!deferChangeUntilClose) return;
        pendingMatterRef.current = internalValue;
        if (!popupOpen) setTempMatter(internalValue);
    }, [internalValue, deferChangeUntilClose, popupOpen]);

    const pageChange = useCallback(
        (event) => {
            const newSkip = event.page.skip;
            if (isNaN(newSkip)) return;

            if (shouldRequestData(newSkip)) requestData(newSkip, filter);

            setData(getCachedData(newSkip));
            setSkip(newSkip);
            skipRef.current = newSkip;
        },
        [filter, getCachedData, requestData, shouldRequestData],
    );

    const onFilterChange = useCallback(
        (event) => {
            const newFilter = event.filter.value || "";
            // Ignore Kendo's no-op re-fire on close so it doesn't refetch page 0.
            if (newFilter === lastFilterRef.current) return;
            lastFilterRef.current = newFilter;
            setFilter(newFilter);
            resetForSearch();
            clearTimeout(filterDebounce.current);
            filterDebounce.current = setTimeout(
                () => requestData(0, newFilter),
                searchDebounceMs,
            );
            props.onFilterChange?.(event);
        },
        [props, resetForSearch, requestData],
    );

    const performManualSearch = useCallback(
        (searchTerm) => {
            const trimmed = searchTerm?.trim();
            if (!trimmed) return;

            clearTimeout(filterDebounce.current);
            lastFilterRef.current = trimmed;
            setFilter(trimmed);
            resetForSearch();
            requestData(0, trimmed);
        },
        [resetForSearch, requestData],
    );

    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (e.key !== "Enter") return;
            const comboInput = comboBoxRef.current?.element?.querySelector("input");
            if (e.target !== comboInput) return;

            const currentValue = e.target.value?.trim();
            if (currentValue && loading) {
                e.preventDefault();
                e.stopPropagation();
                performManualSearch(currentValue);
            }
        };
        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    }, [loading, performManualSearch]);

    const onChange = useCallback(
        (event) => {
            if (deferChangeUntilClose) {
                if (!popupOpen) {
                    const newValue = pickMatterFromEvent(event);
                    if (!controlled) setInternalValue(newValue);
                    onChangematter?.({
                        ...event,
                        value: newValue,
                        target: { ...event?.target, value: newValue, name: props?.name },
                    });
                    return;
                }
                const newValue = pickMatterFromEvent(event);
                pendingMatterRef.current = newValue;
                setTempMatter(newValue);
                return;
            }
            const newValue = event.target.value;
            if (!controlled) setInternalValue(newValue);
            onChangematter?.(event);
        },
        [controlled, onChangematter, deferChangeUntilClose, popupOpen, props?.name],
    );

    const { onOpen: userOnOpen, onClose: userOnClose } = props;

    const onComboOpen = useCallback(
        (e) => {
            if (deferChangeUntilClose) {
                setPopupOpen(true);
                setTempMatter(internalValue);
                pendingMatterRef.current = internalValue;
            }
            // Open filtered to the selection so it's visible in a short list; clear to browse all.
            const selectedRef = internalValue?.[textField];
            if (selectedRef && !filter) {
                clearTimeout(filterDebounce.current);
                lastFilterRef.current = selectedRef;
                setFilter(selectedRef);
                resetForSearch();
                requestData(0, selectedRef);
            }
            userOnOpen?.(e);
        },
        [deferChangeUntilClose, internalValue, userOnOpen, filter, resetForSearch, requestData],
    );

    const onComboClose = useCallback(
        (e) => {
            if (deferChangeUntilClose) {
                setPopupOpen(false);
                const committed = pendingMatterRef.current;
                // Commit only on an actual change; an unchanged close churns/remounts the parent.
                const changed =
                    (committed?.[keyField] ?? null) !== (internalValue?.[keyField] ?? null);
                if (changed) {
                    if (!controlled) setInternalValue(committed);
                    onChangematter?.({ ...e, value: committed, target: { ...e?.target, value: committed } });
                }
            }
            userOnClose?.(e);
        },
        [deferChangeUntilClose, controlled, onChangematter, userOnClose, internalValue],
    );

    const styleObject = {
        width: props.width != null ? `${props.width}px` : "300px",
        minWidth: 0,
        borderRadius: props.borderRadius || undefined,
    };

    const displayValue = deferChangeUntilClose && popupOpen ? tempMatter : internalValue;

    return (
        <ComboBox
            ref={comboBoxRef}
            loading={loading}
            required={props.required}
            data={data}
            value={displayValue}
            onOpen={onComboOpen}
            onChange={onChange}
            onClose={onComboClose}
            dataItemKey={keyField}
            disabled={props.disabled}
            textField={textField}
            placeholder="Select Matter"
            svgIcon={props.svgIcon}
            filterable
            onFilterChange={onFilterChange}
            virtual={{ pageSize, skip, total }}
            onPageChange={pageChange}
            style={{ ...styleObject, ...props.style, border: props.error && "1px solid red" }}
            itemRender={itemRender}
            className={(props.className ?? "") + " px-2"}
        />
    );
};

export default MattersCombobox;
